import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { asArray } from "@/lib/arrays";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Award, Download, Calendar, BookOpen, CheckCircle, FileText, ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const CertificatesPage = () => {
  const [certificates, setCertificates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [certsRes, coursesRes] = await Promise.all([
        axios.get(`${API}/certificates`, { withCredentials: true }),
        axios.get(`${API}/courses`, { withCredentials: true })
      ]);
      const certificateItems = asArray(certsRes.data, "certificates");
      const courseItems = asArray(coursesRes.data, "courses");
      setCertificates(certificateItems);
      setCourses(courseItems);

      // Fetch progress for each course
      const progResults = await Promise.all(
        courseItems.map(c => axios.get(`${API}/progress/${c.course_id}`, { withCredentials: true }).catch(() => ({ data: null })))
      );
      const pMap = {};
      courseItems.forEach((c, i) => { pMap[c.course_id] = progResults[i].data; });
      setProgressMap(pMap);
    } catch (err) {
      console.error("Error fetching certificate data:", err); // eslint-disable-line no-console
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generateCert = async (courseId) => {
    setGenerating(courseId);
    try {
      await axios.post(`${API}/certificates/generate/${courseId}`, {}, { withCredentials: true });
      toast.success("Certificate generated! Download it below.");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate certificate");
    }
    finally { setGenerating(null); }
  };

  const downloadCert = (certNumber) => {
    window.open(`${API}/certificates/download/${certNumber}`, "_blank");
  };

  if (loading) {
    return (
      <DashboardLayout title="Certificates">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[#00FF88] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const certMap = {};
  certificates.forEach(c => { certMap[c.course_id] = c; });

  return (
    <DashboardLayout title="Certificates">
      <div className="space-y-5" data-testid="certificates-page">
        {/* Header */}
        <div className="terminal-card rounded-lg p-6" data-testid="cert-header">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-[#FFCC00]/10 flex items-center justify-center">
              <Award className="w-6 h-6 text-[#FFCC00]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white font-outfit">Your Certificates</h2>
              <p className="text-xs text-gray-500 dark:text-[#94A3B8] font-plex">Complete a course to 100% to earn your Jamadhan certificate</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-gray-50 dark:bg-white/[0.02] rounded-lg px-4 py-2">
              <p className="text-xs text-gray-500 dark:text-[#94A3B8] font-jetbrains">Earned</p>
              <p className="text-xl font-bold text-[#00FF88] font-jetbrains" data-testid="cert-earned-count">{certificates.length}</p>
            </div>
            <div className="bg-gray-50 dark:bg-white/[0.02] rounded-lg px-4 py-2">
              <p className="text-xs text-gray-500 dark:text-[#94A3B8] font-jetbrains">Available</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white font-jetbrains">{courses.length}</p>
            </div>
          </div>
        </div>

        {/* Earned Certificates */}
        {certificates.length > 0 && (
          <div>
            <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#00FF88] uppercase mb-3">EARNED CERTIFICATES</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="earned-certs">
              {certificates.map(cert => (
                <div key={cert.certificate_id} className="terminal-card rounded-lg p-5 border-[#FFCC00]/10" data-testid={`cert-${cert.certificate_id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#FFCC00]/10 flex items-center justify-center">
                        <Award className="w-5 h-5 text-[#FFCC00]" />
                      </div>
                      <div>
                        <h3 className="text-sm font-outfit font-semibold text-gray-900 dark:text-white">{cert.course_title}</h3>
                        <p className="text-xs text-gray-500 dark:text-[#94A3B8] font-jetbrains mt-0.5">{cert.certificate_number}</p>
                      </div>
                    </div>
                    <Badge className="bg-[#00FF88]/10 text-[#00FF88] border-0 text-[10px]"><CheckCircle className="w-3 h-3 mr-1" />Earned</Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-[#94A3B8] font-plex mb-4">
                    <Calendar className="w-3 h-3" />
                    Issued: {new Date(cert.issued_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                  </div>
                  <Button onClick={() => downloadCert(cert.certificate_number)} className="w-full bg-[#FFCC00] text-black font-bold hover:brightness-110 rounded-sm" data-testid={`download-cert-${cert.certificate_id}`}>
                    <Download className="w-4 h-4 mr-2" />Download PDF
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Course Progress → Certificate */}
        <div>
          <p className="font-jetbrains text-[10px] tracking-[0.2em] text-[#00F0FF] uppercase mb-3">COURSE PROGRESS</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="course-progress">
            {courses.map(course => {
              const hasCert = !!certMap[course.course_id];
              const prog = progressMap[course.course_id];
              const pct = Math.round(prog?.progress_percentage || 0);
              const completedCount = prog?.completed_lessons?.length || 0;
              const isComplete = pct >= 100;

              return (
                <div key={course.course_id} className={`terminal-card rounded-lg p-5 ${hasCert ? "border-[#FFCC00]/10" : ""}`} data-testid={`course-cert-${course.course_id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      hasCert ? "bg-[#FFCC00]/10" : isComplete ? "bg-[#00FF88]/10" : "bg-white/[0.04]"
                    }`}>
                      {hasCert ? <Award className="w-5 h-5 text-[#FFCC00]" /> :
                        isComplete ? <CheckCircle className="w-5 h-5 text-[#00FF88]" /> :
                        <BookOpen className="w-5 h-5 text-gray-500 dark:text-[#94A3B8]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-outfit font-semibold text-gray-900 dark:text-white truncate">{course.title}</p>
                      <p className="text-xs text-gray-500 dark:text-[#94A3B8] font-plex">{course.is_free ? "Free" : `₹${(course.price || 0).toLocaleString()}`}</p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-500 dark:text-[#94A3B8] font-plex">{completedCount} lessons completed</span>
                      <span className={`font-jetbrains font-bold ${isComplete ? "text-[#00FF88]" : "text-gray-900 dark:text-white"}`}>{pct}%</span>
                    </div>
                    <ProgressBar value={pct} className="h-2 bg-white/[0.04]" />
                  </div>

                  {hasCert ? (
                    <Button onClick={() => downloadCert(certMap[course.course_id].certificate_number)}
                      className="w-full bg-[#FFCC00] text-black font-bold hover:brightness-110 rounded-sm text-xs" data-testid={`download-cert-${course.course_id}`}>
                      <Download className="w-3.5 h-3.5 mr-2" />Download Certificate
                    </Button>
                  ) : isComplete ? (
                    <Button onClick={() => generateCert(course.course_id)} disabled={!!generating}
                      className="w-full bg-[#00FF88] text-black font-bold hover:brightness-110 rounded-sm text-xs" data-testid={`gen-cert-${course.course_id}`}>
                      {generating === course.course_id ? "Generating..." : <><Award className="w-3.5 h-3.5 mr-2" />Generate Certificate</>}
                    </Button>
                  ) : (
                    <Link to={`/course/${course.course_id}`}>
                      <Button className="w-full bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] text-gray-500 dark:text-[#94A3B8] hover:bg-white/[0.06] rounded-sm text-xs" data-testid={`continue-${course.course_id}`}>
                        <ArrowRight className="w-3.5 h-3.5 mr-2" />Continue Learning ({100 - pct}% remaining)
                      </Button>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CertificatesPage;
